import React, { useEffect, useRef, useState } from "react";
import {
  RoomContext,
  SessionProvider,
  useSession,
} from "@livekit/components-react";
import { TokenSource } from "livekit-client";
import { AgentPanel } from "../AgentPanel/AgentPanel";
import type { NextConversation } from "../useCallMachine";
import { getUser, type UserData } from "../../auth";
import { getBrowserLocationIfGranted, type BrowserLocation } from "../../geolocation";
import { PAGE_INFO_METHOD, currentPageInfo } from "../../page-info";
import type { TranscriptTurn } from "../agentRejoin";

function buildInitials(user: UserData | null | undefined): string | null {
  if (!user?.firstName) return null;
  const first = user.firstName[0] ?? "";
  const last = user.lastName?.[0] ?? "";
  return (first + last).toUpperCase() || null;
}

export function AgentSession({
  serverUrl,
  agentName,
  avatarUrl,
  user,
  opened,
  onClose,
  closeLabel,
  onConversationEnded,
  panelExpanded,
  onToggleExpand,
  onSetExpanded,
  onVoiceStateChange,
  startCallSignal,
  initialMessage,
}: {
  serverUrl: string;
  agentName: string;
  avatarUrl?: string;
  user?: UserData | null;
  // False until the user opens the panel (always true in the mobile embed,
  // where loading the page IS opening the chat). Mounting the panel starts the
  // session; until then only the connection warm-up above runs.
  opened: boolean;
  onClose: () => void;
  closeLabel?: string;
  onConversationEnded: (next?: NextConversation) => void;
  panelExpanded: boolean;
  onToggleExpand: () => void;
  onSetExpanded: (expanded: boolean) => void;
  onVoiceStateChange?: (active: boolean) => void;
  startCallSignal?: number;
  initialMessage?: string;
}) {
  // Custom token source: sends user data in the POST body. The server uses it
  // to set room metadata (read by the Python agent) and to fetch player context
  // via M2M auth. getUser() reads live from window.__courtsapp_user on each
  // request so sign-in/out on the host page is picked up without a widget reload.
  const locationPromiseRef = useRef<Promise<BrowserLocation | null> | null>(null);
  if (locationPromiseRef.current === null) {
    locationPromiseRef.current = getBrowserLocationIfGranted().catch(() => null);
  }

  // Whose token the (page-load prefetched, SDK-cached) token is. If the host
  // page signs someone in or out between page load and opening the chat, the
  // cached token carries the wrong identity, so ensureSession refetches.
  const fetchedForUserIdRef = useRef<string | null | undefined>(undefined);
  // The participant token of the room we're in. /api/rejoin takes it back as
  // proof the caller owns the session (and for the agent's session metadata).
  const credentialsRef = useRef<{ token: string; room: string } | null>(null);
  const tokenSource = React.useMemo(
    () =>
      TokenSource.custom(async () => {
        const live = getUser() ?? user ?? null;
        fetchedForUserIdRef.current = live?.id ?? null;
        const location = await (locationPromiseRef.current ?? Promise.resolve(null));
        const resp = await fetch(`${serverUrl}/api/connection-details`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            room_config: { agents: [{ agent_name: agentName }] },
            ...(live && {
              user: {
                id: live.id,
                firstName: live.firstName,
                lastName: live.lastName,
                email: live.email,
                phoneNumber: live.phoneNumber,
              },
            }),
            ...(location && { location }),
            ...(initialMessageRef.current && { initial_message: initialMessageRef.current }),
          }),
        });
        initialMessageRef.current = null;
        if (!resp.ok) {
          throw new Error(`Token fetch failed: ${resp.status}`);
        }
        const details = await resp.json();
        credentialsRef.current = {
          token: details.participant_token,
          room: details.room_name,
        };
        return details;
      }),
    [serverUrl, agentName, user],
  );
  const userInitials = buildInitials(getUser() ?? user ?? null);
  // agentConnectTimeoutMilliseconds: SDK flips agent.state to 'failed' (with
  // failureReasons populated) if no agent joins within the window. AgentPanel
  // watches that state below — no hand-rolled setTimeout watchdog.
  const session = useSession(tokenSource, {
    agentName,
    agentConnectTimeoutMilliseconds: 30_000,
  });
  const [sessionStarted, setSessionStarted] = useState(false);
  const startingRef = useRef(false);

  // Chip-tap / first-typed-message — set before ensureSession so the tokenSource
  // closure picks it up and bakes it into room metadata. The agent reads it
  // at entrypoint time and responds to the intent instead of a generic greeting.
  const initialMessageRef = useRef<string | null>(null);
  const setInitialMessage = React.useCallback((msg: string) => {
    initialMessageRef.current = msg;
  }, []);

  // Page load: useSession() itself fetches a token and calls
  // room.prepareConnection() once on mount (DNS/TLS/signal warm-up). That does
  // NOT join a room or dispatch the agent — only session.start() does, and
  // that happens when the panel first opens (AgentPanel mounts) so the
  // greeting is fast. A page view alone never starts a session.
  // Courtney calls getPageInfo on this participant to see the open page.
  // Registered for the life of the room, including before the user speaks.
  useEffect(() => {
    const room = session.room;
    room.registerRpcMethod(PAGE_INFO_METHOD, async () =>
      JSON.stringify(currentPageInfo()),
    );
    return () => {
      room.unregisterRpcMethod(PAGE_INFO_METHOD);
    };
  }, [session.room]);

  // Defer session.start() until the user sends, taps a chip or taps the mic —
  // otherwise the agent dispatches and greets immediately.
  // Note: useSession handles room.disconnect() on unmount internally, so no manual
  // cleanup here. Adding `session` to a cleanup effect's deps would call end()
  // mid-connect, since useSession re-creates its return value on every
  // connectionState transition.
  const ensureSession = React.useCallback(
    async (withMic: boolean) => {
      if (startingRef.current) return;
      startingRef.current = true;
      setSessionStarted(true);
      try {
        const nowUserId = (getUser() ?? user ?? null)?.id ?? null;
        if (fetchedForUserIdRef.current !== undefined && fetchedForUserIdRef.current !== nowUserId) {
          await tokenSource.fetch({ agentName }, true); // force: drop the stale prefetched token
        }
        await session.start({ tracks: { microphone: { enabled: withMic } } });
      } catch (err) {
        // Reset flags on failure — without this we'd be wedged in a
        // "starting forever" state with the early-return blocking retries.
        startingRef.current = false;
        setSessionStarted(false);
        throw err;
      }
    },
    [session, tokenSource, agentName, user],
  );

  // The agent dropped mid-chat: ask the widget server for a new one in this
  // same room. Resolves once the request is accepted (the caller then waits for
  // the agent to actually join); throws on any rejection so the caller retries.
  const requestAgentRejoin = React.useCallback(
    async (transcript: TranscriptTurn[], voice: boolean) => {
      const creds = credentialsRef.current;
      const roomName = session.room.name;
      if (!creds || !roomName || creds.room !== roomName) {
        throw new Error("No session credentials for this room");
      }
      const resp = await fetch(`${serverUrl}/api/rejoin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          room_name: roomName,
          token: creds.token,
          transcript,
          voice,
        }),
        signal: AbortSignal.timeout(10_000),
      });
      if (!resp.ok) throw new Error(`Rejoin failed: ${resp.status}`);
    },
    [serverUrl, session.room],
  );

  const endSession = React.useCallback(async () => {
    startingRef.current = false;
    setSessionStarted(false);
    await session.end();
  }, [session]);

  // RoomContext is required by SDK hooks like useTrackToggle and
  // useLocalParticipant. SessionProvider sets SessionContext only.
  return (
    <SessionProvider session={session}>
      <RoomContext.Provider value={session.room}>
        {opened && (
          <AgentPanel
            serverUrl={serverUrl}
            avatarUrl={avatarUrl}
            userInitials={userInitials}
            onClose={onClose}
            closeLabel={closeLabel}
            ensureSession={ensureSession}
            endSession={endSession}
            sessionStarted={sessionStarted}
            onConversationEnded={onConversationEnded}
            setInitialMessage={setInitialMessage}
            panelExpanded={panelExpanded}
            onToggleExpand={onToggleExpand}
            onSetExpanded={onSetExpanded}
            onVoiceStateChange={onVoiceStateChange}
            startCallSignal={startCallSignal}
            initialMessage={initialMessage}
            requestAgentRejoin={requestAgentRejoin}
            greetingName={(getUser() ?? user ?? null)?.firstName ?? null}
          />
        )}
      </RoomContext.Provider>
    </SessionProvider>
  );
}
